// Single integration point for every call this app makes to its own backends (business-gateway,
// edge-signer, oauth-proxy). Nothing outside this file should call fetch() against one of these
// three base URLs directly — see api/gatewayApi.ts, api/signerApi.ts, api/oauthProxyApi.ts for the
// typed SDK built on top of this.
import { Capacitor } from '@capacitor/core';

export type ApiBackend = 'gateway' | 'signer' | 'oauthProxy';

// Shared default origin for every backend, in precedence order:
//   1. VITE_API_URL — explicit override, applies to all three backends at once
//   2. hostname === localhost/127.0.0.1 — business-gateway's own `wrangler dev` port (edge-signer
//      and oauth-proxy share this origin in prod; only a standalone signer `wrangler dev` uses its
//      own 8787, set via VITE_SIGNER_URL for that case)
//   3. otherwise — derive https://api.<hostname> from the page origin at runtime
//
// A native (Capacitor) build's WebView reports `localhost` as its hostname regardless of what
// backend it should actually talk to — there's no real page origin to derive from on-device — so
// derivation would silently resolve to the wrong (dev) origin in a shipped app. Fail loudly
// instead: native builds must set VITE_API_URL explicitly.
function derivedDefaultOrigin(): string {
  const override = import.meta.env.VITE_API_URL;
  if (override) return override;
  if (Capacitor.isNativePlatform()) {
    throw new Error('VITE_API_URL must be set for native (Capacitor) builds — see .env.example.');
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8788';
  return `https://api.${hostname}`;
}

const DEFAULT_ORIGIN = derivedDefaultOrigin();

// Per-backend env vars take precedence over the shared default — only for local dev running a
// package's own standalone `wrangler dev` on its own port. Leave unset otherwise.
// `||`, not `??` — an env var set to an empty string (e.g. `VITE_GATEWAY_URL=` in .env, meaning
// "unset") must fall through to DEFAULT_ORIGIN; `??` would treat '' as a defined override.
const BASE_URLS: Record<ApiBackend, string> = {
  gateway: import.meta.env.VITE_GATEWAY_URL || DEFAULT_ORIGIN,
  oauthProxy: import.meta.env.VITE_OAUTH_PROXY_URL || DEFAULT_ORIGIN,
  signer: import.meta.env.VITE_SIGNER_URL || DEFAULT_ORIGIN,
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly backend: ApiBackend,
    public readonly path: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestLogEntry {
  at: number;
  backend: ApiBackend;
  error?: string;
  method: string;
  ms: number;
  ok: boolean;
  path: string;
  status: number | null;
}

// ── Request capture — the "one integration point" for observing every call ─────────────────────
// A ring buffer plus subscriber list. Not tied to any specific logger/analytics tool on purpose:
// register a Sentry breadcrumb, a debug console, or an offline-queue hook here later without
// touching any of the ~25 call sites that use the SDK below.
const MAX_LOG_ENTRIES = 50;
const requestLog: ApiRequestLogEntry[] = [];
type RequestInterceptor = (entry: ApiRequestLogEntry) => void;
const interceptors: RequestInterceptor[] = [];

export function onApiRequest(fn: RequestInterceptor): () => void {
  interceptors.push(fn);
  return () => {
    const i = interceptors.indexOf(fn);
    if (i !== -1) interceptors.splice(i, 1);
  };
}

export function getRecentApiRequests(): ApiRequestLogEntry[] {
  return [...requestLog];
}

function recordRequest(entry: ApiRequestLogEntry) {
  requestLog.push(entry);
  if (requestLog.length > MAX_LOG_ENTRIES) requestLog.shift();
  for (const fn of interceptors) {
    try {
      fn(entry);
    } catch {
      /* an interceptor's own failure must never break a real request */
    }
  }
}

// ── Auth ─────────────────────────────────────────────────────────────────────────────────────
// Single source of truth for the session-token header — replaces four independent
// reimplementations (useSubscription.ts, useTeamMembers.ts, useTeamSchedule.ts, plus two
// hand-rolled inline versions in AuditLog.vue/AccountSettings.vue) that had drifted into two
// slightly different shapes.
function authHeader(): Record<string, string> {
  const token = localStorage.getItem('punchlog_session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface RequestOptions {
  /** Attach the session-token Authorization header. Default true for gateway, false otherwise —
   * pass explicitly per SDK method rather than relying on the default when it matters. */
  auth?: boolean;
  /** JSON-serializable request body. */
  body?: unknown;
  /** Query-string params; falsy values are omitted. */
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
}

function buildUrl(backend: ApiBackend, path: string, query?: RequestOptions['query']): string {
  const base = BASE_URLS[backend];
  const qs = query
    ? Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
    : [];
  const search = qs.length
    ? `?${new URLSearchParams(qs.map(([k, v]) => [k, String(v)])).toString()}`
    : '';
  return `${base}${path}${search}`;
}

function buildHeaders(auth?: boolean): Record<string, string> {
  return { 'Content-Type': 'application/json', ...(auth ? authHeader() : {}) };
}

async function parseResponseBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function extractErrorMessage(json: unknown, res: Response): string {
  if (json && typeof json === 'object' && 'error' in json && typeof json.error === 'string') {
    return json.error;
  }
  return res.statusText;
}

/**
 * The one function that actually calls fetch() against our own backends. Every SDK method in
 * gatewayApi.ts/signerApi.ts/oauthProxyApi.ts funnels through this.
 *
 * Always throws ApiError on a non-2xx response or network failure — callers decide UX (toast,
 * silent swallow, etc.) at the call site, not here. This replaces the pre-consolidation mix of
 * throw / set-error-ref / silently-return-on-failure that had drifted per file.
 */
export async function apiRequest<T = unknown>(
  backend: ApiBackend,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(backend, path, opts.query);
  const headers = buildHeaders(opts.auth);
  const start = Date.now();

  let res: Response;
  try {
    res = await fetch(url, {
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      headers,
      method,
      signal: opts.signal,
    });
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Network error';
    recordRequest({
      at: start,
      backend,
      error: message,
      method,
      ms: Date.now() - start,
      ok: false,
      path,
      status: null,
    });
    throw new ApiError(message, 0, backend, path);
  }

  const ms = Date.now() - start;
  const json = await parseResponseBody(res);

  recordRequest({
    at: start,
    backend,
    error: res.ok ? undefined : extractErrorMessage(json, res),
    method,
    ms,
    ok: res.ok,
    path,
    status: res.status,
  });

  if (!res.ok) {
    throw new ApiError(extractErrorMessage(json, res), res.status, backend, path);
  }
  return json as T;
}
